import { useForm } from '@tanstack/react-form';
import { confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth';
import { Link, useLoaderData, useNavigate, useRouteError } from 'react-router';
import { z } from 'zod';

import { Button, Label } from '@workspace/shadcn';
import {
  FieldMessage,
  FormMessage,
  PasswordInput,
  notifyPasswordChanged,
} from '@workspace/ui';

import { AuthShell } from '../../components/auth-shell.tsx';
import { auth } from '../../firebase.tsx';

const schema = z
  .object({
    password: z
      .string()
      .min(8, { error: 'Password must be at least 8 characters' }),
    confirmPassword: z.string().min(1, { error: 'Confirm your password' }),
  })
  .refine((data) => data.password === data.confirmPassword, {
    error: 'Passwords do not match',
    path: ['confirmPassword'],
  });

interface ResetPasswordLoaderData {
  oobCode: string;
}

export default function ResetPassword() {
  const navigate = useNavigate();
  const { oobCode } = useLoaderData() as ResetPasswordLoaderData;

  const form = useForm({
    defaultValues: { password: '', confirmPassword: '' },
    validators: {
      onSubmit: schema,
      onSubmitAsync: schema.superRefine(async (data, ctx) => {
        try {
          const email = await verifyPasswordResetCode(auth, oobCode);
          await confirmPasswordReset(auth, oobCode, data.password);
          await notifyPasswordChanged(email);
          navigate('/password-reset');
        } catch {
          ctx.addIssue({
            code: 'custom',
            message: 'We could not change your password. Try again.',
            path: ['form'],
          });
        }
      }),
    },
  });

  return (
    <AuthShell
      title="Set a new password"
      subtitle="Choose a password you have not used before."
    >
      <form
        className="mt-6 space-y-4"
        onSubmit={async (event) => {
          event.preventDefault();
          event.stopPropagation();
          await form.handleSubmit();
        }}
      >
        <form.Field
          name="password"
          children={(field) => (
            <div className="grid w-full items-center gap-2">
              <Label htmlFor="password">New password</Label>
              <PasswordInput
                id="password"
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(event) => field.handleChange(event.target.value)}
              />
              <FieldMessage errors={field.state.meta.errors} />
            </div>
          )}
        />

        <form.Field
          name="confirmPassword"
          children={(field) => (
            <div className="grid w-full items-center gap-2">
              <Label htmlFor="confirmPassword">Confirm password</Label>
              <PasswordInput
                id="confirmPassword"
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(event) => field.handleChange(event.target.value)}
              />
              <FieldMessage errors={field.state.meta.errors} />
            </div>
          )}
        />

        <form.Subscribe
          selector={(state) => [state.canSubmit, state.isSubmitting]}
          children={([canSubmit, isSubmitting]) => (
            <Button type="submit" disabled={!canSubmit} className="w-full">
              {isSubmitting ? 'Saving...' : 'Save password'}
            </Button>
          )}
        />
        <form.Subscribe
          selector={(state) => state.errors}
          children={(errors) => <FormMessage errors={errors} />}
        />
      </form>
    </AuthShell>
  );
}

export function ResetPasswordError() {
  const error = useRouteError();
  const message =
    error instanceof Error
      ? error.message
      : 'This reset link is invalid or has expired.';

  return (
    <AuthShell title="Something went wrong">
      <div className="mt-6 space-y-4">
        <p className="text-destructive text-center">{message}</p>
        <div className="text-center">
          <Link to="/forgot-password" className="text-foreground underline">
            Request a new reset link
          </Link>
        </div>
      </div>
    </AuthShell>
  );
}
